/**
 * Opaque campaign token codec (2 bytes).
 *
 * A curated SRP landing (reached via a mailer QR code) carries a single query
 * param `?c=XXXX`. The token packs a campaign id and a variant id into a 16-bit
 * value, obfuscates it with a fixed XOR mask, and encodes it as a fixed-width
 * 4-char Crockford base32 string.
 *
 * Two goals:
 *  1. Opacity — neither the cohort nor the campaign intent is readable from the
 *     URL, and sequential campaigns do not produce adjacent-looking tokens
 *     (the XOR is obfuscation, NOT security).
 *  2. Compactness — always 4 chars, case-insensitive, OCR/print safe.
 *
 * Human-facing notation is 1-based and dotted: `campaign.variant`, e.g. "1.1"
 * means campaign 1, variant 1. Marketing/ops reference campaigns this way;
 * `formatCampaign` / `parseHumanCampaign` convert to/from it. Only the opaque
 * `?c=` token ever appears in a URL.
 *
 * Presence of a valid token is the signal that the visitor arrived via a
 * campaign link: the standard onboarding drawer is suppressed and a campaign
 * welcome treatment (selected by the tuple) is shown instead. No cohort naming
 * lives in the URL or the design system.
 *
 * Layout of the 16-bit value (stored 0-based; API is 1-based):
 *
 *    15                    4 3        0
 *   [   campaign-1 (12b)     ][ var-1(4b) ]
 *     campaign 1 – 4096        variant 1 – 16
 */

// Crockford base32 alphabet (no I, L, O, U — avoids visual/OCR ambiguity).
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const TOKEN_LENGTH = 4 // 32^4 = 2^20 comfortably covers 16 bits
const MASK = 0x5a3c // fixed obfuscation mask — must match the generator
const VARIANT_BITS = 4
const VARIANT_MASK = (1 << VARIANT_BITS) - 1 // 0x0F
// 1-based inclusive bounds.
const MAX_CAMPAIGN = 1 << (16 - VARIANT_BITS) // 4096
const MAX_VARIANT = VARIANT_MASK + 1 // 16
const CAMPAIGN_PARAM = 'c'

export interface CampaignToken {
  /** Campaign id, 1 – 4096. Tracking dimension; may also branch content. */
  campaign: number
  /** Variant id, 1 – 16. Selects the welcome treatment. */
  variant: number
}

/**
 * Campaign alias registry — the authoritative map of campaign number to a
 * descriptive, human-readable alias. Every live campaign MUST be registered
 * here: it is the single source of truth shared by the SRP, the CLI generator,
 * and analytics/ops. Add a line when a campaign launches.
 *
 * Aliases are lowercase snake_case and should read as
 * <asset>_<intent>_<channel>_<year>.
 */
export const CAMPAIGN_ALIASES: Readonly<Record<number, string>> = {
  1: 'foreclosure_reactivation_mailer_2026',
  // 2: '<next campaign alias>',
}

/** Descriptive alias for a campaign number, or undefined if unregistered. */
export const getCampaignAlias = (campaign: number): string | undefined =>
  CAMPAIGN_ALIASES[campaign]

/** Reverse lookup: campaign number for a descriptive alias, or undefined. */
export const getCampaignByAlias = (alias: string): number | undefined => {
  const entry = Object.entries(CAMPAIGN_ALIASES).find(([, a]) => a === alias)
  return entry ? Number(entry[0]) : undefined
}

/** Whether a campaign number has a registered alias. */
export const isRegisteredCampaign = (campaign: number): boolean =>
  campaign in CAMPAIGN_ALIASES

// Crockford decode leniency: I/L -> 1, O -> 0, case-insensitive.
const normalizeChar = (ch: string): string => {
  const c = ch.toUpperCase()
  if (c === 'I' || c === 'L') return '1'
  if (c === 'O') return '0'
  return c
}

const inRange = ({ campaign, variant }: CampaignToken): boolean =>
  Number.isInteger(campaign) &&
  Number.isInteger(variant) &&
  campaign >= 1 &&
  campaign <= MAX_CAMPAIGN &&
  variant >= 1 &&
  variant <= MAX_VARIANT

/**
 * Encode a campaign + variant (1-based) into the opaque 4-char token. Used by
 * the campaign/mailer link generator; kept here so encode and decode never
 * drift.
 */
export const encodeCampaign = (token: CampaignToken): string => {
  if (!inRange(token)) {
    throw new RangeError(
      `campaign must be an integer 1-${MAX_CAMPAIGN} and variant 1-${MAX_VARIANT}`
    )
  }
  const packed =
    (((token.campaign - 1) << VARIANT_BITS) | (token.variant - 1)) & 0xffff
  let x = (packed ^ MASK) & 0xffff
  let out = ''
  for (let i = 0; i < TOKEN_LENGTH; i += 1) {
    out = ALPHABET[x & 31] + out
    x >>>= 5
  }
  return out
}

/**
 * Decode a raw token back to { campaign, variant } (1-based). Returns null for
 * anything that is not a well-formed in-range token — callers treat null as "no
 * campaign", so garbage or tampering silently falls back to default behavior.
 */
export const decodeCampaign = (raw?: string | null): CampaignToken | null => {
  if (!raw || raw.length !== TOKEN_LENGTH) return null
  let x = 0
  for (const ch of raw) {
    const idx = ALPHABET.indexOf(normalizeChar(ch))
    if (idx === -1) return null
    x = x * 32 + idx
  }
  if (x > 0xffff) return null
  const packed = (x ^ MASK) & 0xffff
  return {
    campaign: (packed >> VARIANT_BITS) + 1,
    variant: (packed & VARIANT_MASK) + 1,
  }
}

/**
 * Read and decode the campaign token from a location search string
 * (e.g. `?c=XXXX&utm_source=...`). Returns null when absent or invalid.
 */
export const readCampaignParam = (
  search?: string | null
): CampaignToken | null => {
  if (!search) return null
  const query = search.startsWith('?') ? search.slice(1) : search
  const value = new URLSearchParams(query).get(CAMPAIGN_PARAM)
  return decodeCampaign(value)
}

/** Human-facing dotted id, e.g. { campaign: 1, variant: 1 } -> "1.1". */
export const formatCampaign = ({ campaign, variant }: CampaignToken): string =>
  `${campaign}.${variant}`

/**
 * Parse the human-facing dotted id into a token. The campaign slot accepts
 * either a number ("1.1") or a registered alias
 * ("foreclosure_reactivation_mailer_2026.1"). Variant is always 1-based.
 * Returns null when malformed or out of range. Convenience for the generator /
 * ops tooling — the URL always uses the opaque token, never this.
 */
export const parseHumanCampaign = (
  input?: string | null
): CampaignToken | null => {
  if (!input) return null
  const trimmed = input.trim()
  const dot = trimmed.lastIndexOf('.')
  if (dot <= 0 || dot === trimmed.length - 1) return null
  const left = trimmed.slice(0, dot)
  const right = trimmed.slice(dot + 1)
  if (!/^\d+$/.test(right)) return null
  const campaign = /^\d+$/.test(left) ? Number(left) : getCampaignByAlias(left)
  if (campaign === undefined) return null
  const token = { campaign, variant: Number(right) }
  return inRange(token) ? token : null
}
